import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';
import * as auth from 'firebase/auth';
import { BehaviorSubject, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { AuditLogService } from './audit-log.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  get userImage(): string {
    return (
      localStorage.getItem('userImage') || 'public/img/userProfileIcon2.png'
    );
  }

  set userImage(userImage: string) {
    localStorage.setItem('userImage', userImage);
  }

  private readonly router = inject(Router);
  private readonly fireAuth = inject(AngularFireAuth);
  private readonly httpClient = inject(HttpClient);
  private readonly auditLogService = inject(AuditLogService);

  private readonly userRolesSubject = new BehaviorSubject<string[]>([]);
  public userRoles$ = this.userRolesSubject.asObservable();

  constructor() {
    this.fireAuth.user.subscribe(async (user) => {
      if (user) {
        try {
          const tokenResult = await user.getIdTokenResult();
          const rolesClaim = tokenResult.claims['roles'] || tokenResult.claims['role'] || [];
          let finalRoles: string[] = [];

          if (Array.isArray(rolesClaim)) {
            finalRoles = rolesClaim;
          } else if (typeof rolesClaim === 'string') {
            finalRoles = [rolesClaim];
          }

          if (finalRoles.length === 0 && user.email?.endsWith('@telecomitalia.it')) {
            finalRoles = ['sales'];
          }

          this.userRolesSubject.next(finalRoles);
        } catch (e) {
          console.error('Error fetching roles', e);
          this.userRolesSubject.next([]);
        }
      } else {
        this.userRolesSubject.next([]);
      }
    });
  }

  hasRole(role: string): boolean {
    return this.userRolesSubject.value.includes(role);
  }

  hasRoles(rolesToCheck: string[]): Observable<boolean> {
    return this.fireAuth.authState.pipe(
      take(1),
      switchMap(user => {
        if (!user) return throwError(() => new Error('Not logged in'));
        return from(user.getIdTokenResult()).pipe(
          map(tokenResult => {
            const rolesClaim = tokenResult.claims['roles'] || tokenResult.claims['role'] || [];
            let userRoles: string[] = [];

            if (Array.isArray(rolesClaim)) {
              userRoles = rolesClaim;
            } else if (typeof rolesClaim === 'string') {
              userRoles = [rolesClaim];
            }

            return rolesToCheck.some(role => userRoles.includes(role));
          }),
          catchError(() => of(false))
        );
      })
    );
  }

  signInWithEmailAndPassword(
    email: string,
    password: string,
    redirectUri: string
  ) {
    return from(this.fireAuth.signInWithEmailAndPassword(email, password)).pipe(
      switchMap(() => this.fireAuth.user),
      catchError(() => {
        this.fireAuth?.signOut();
        return throwError(() => new Error('Invalid credential'));
      }),
      tap((user) => {
        this.postLoginAction(user.email, redirectUri);
      })
    );
  }

  signInWithGoogle(redirectUri: string) {
    return from(
      this.fireAuth.signInWithPopup(new auth.GoogleAuthProvider())
    ).pipe(
      switchMap(() => this.fireAuth.user),
      catchError(() => {
        this.fireAuth.signOut();
        return throwError(() => new Error('Invalid credential'));
      }),
      tap((user) => {
        this.userImage = user?.photoURL;

        this.postLoginAction(user.email, redirectUri);
      })
    );
  }

  signInWithMicrosoft(redirectUri: string) {
    const provider = new auth.OAuthProvider('microsoft.com');
    provider.setCustomParameters({
      // Force re-consent.
      prompt: 'select_account',
      tenant: 'telecomitalia.it',
    });

    return from(this.fireAuth.signInWithPopup(provider)).pipe(
      map((result) => result.credential as auth.OAuthCredential),
      catchError((error) => {
        this.fireAuth.signOut();
        return throwError(() => new Error('Invalid credential'));
      }),

      tap((credential: auth.OAuthCredential) => {
        this.setMicrosoftUserImage(credential.accessToken);
        this.fireAuth.user.subscribe(user => {
          this.postLoginAction(user.email, redirectUri);
        })
      })
    );
  }

  logout() {
    this.fireAuth.currentUser.then((user) => {
      this.auditLogService.logout(user.email);
      this.fireAuth.signOut();
      localStorage.clear();
      this.router.navigate(['login']);
    });
  }

  private setMicrosoftUserImage(accessToken: string) {
    const url = 'https://graph.microsoft.com/v1.0/me/photo/$value';

    const headers = {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'image/jpg',
    };

    return this.httpClient
      .get(url, {
        responseType: 'blob',
        headers,
      })
      .subscribe((blobImage: Blob) => {
        const reader = new FileReader();
        reader.readAsDataURL(blobImage);
        reader.onloadend = () => {
          this.userImage = reader.result as string;
        };
      });
  }

  private postLoginAction(userEmail: string, redirectUri: string) {
    this.auditLogService.login(userEmail);

    const redirectToUrl = new URL(redirectUri, location.origin);
    const params = new URLSearchParams(redirectToUrl.search);
    const queryParams: any = {};
    params.forEach((value, key) => (queryParams[key] = value));
    this.router.navigate([redirectToUrl.pathname], { queryParams });
  }
}
